import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { getQuotations, updateQuotationStatus, createQuotation, getAllProducts, getCustomers, downloadQuotationPdf, sendQuotationEmail } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { FileText, CheckCircle, XCircle, Clock, Plus, Search, Trash2, Download, Printer, Mail } from 'lucide-react';

const Quotations = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quotations, setQuotations] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Customer server-side search
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const customerDebounceRef = useRef(null);

  const [cart, setCart] = useState([]);
  const default30Days = () => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  };
  const [validUntil, setValidUntil] = useState(default30Days);
  const [notes, setNotes] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [quotationsData, productsData] = await Promise.all([
        getQuotations(),
        getAllProducts(),
      ]);
      setQuotations(quotationsData);
      setProducts(productsData);
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Customer search debounce
  useEffect(() => {
    if (customerQuery.length < 2) { setCustomerResults([]); setShowCustomerDropdown(false); return; }
    clearTimeout(customerDebounceRef.current);
    customerDebounceRef.current = setTimeout(async () => {
      setCustomerSearching(true);
      try {
        const data = await getCustomers({ q: customerQuery, per_page: 8 });
        setCustomerResults(data.customers || data || []);
        setShowCustomerDropdown(true);
      } catch { setCustomerResults([]); }
      finally { setCustomerSearching(false); }
    }, 400);
    return () => clearTimeout(customerDebounceRef.current);
  }, [customerQuery]);

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    setCustomerQuery(`${c.name} ${c.last_name || ''}`.trim());
    setShowCustomerDropdown(false);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateQuotationStatus(id, status);
      toast.success('Estado actualizado');
      loadData();
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  const handleConvertir = (quotation) => {
    navigate(`/pos?quotation_id=${quotation.id}`);
  };

  const handleDownload = async (q) => {
    setDownloadingId(q.id);
    try {
      const blob = await downloadQuotationPdf(q.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cotizacion_${q.quotation_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar PDF'); }
    finally { setDownloadingId(null); }
  };

  const openEmailModal = (q) => {
    setEmailModal(q);
    setEmailAddress(q.customer_email || '');
  };

  const handleSendEmail = async () => {
    if (!emailAddress || !emailAddress.includes('@')) { toast.error('Correo inválido'); return; }
    setSendingEmail(true);
    try {
      await sendQuotationEmail(emailModal.id, emailAddress);
      toast.success(`Cotización enviada a ${emailAddress}`);
      setEmailModal(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al enviar correo');
    } finally {
      setSendingEmail(false);
    }
  };

  const handlePrint = async (q) => {
    setDownloadingId(q.id);
    try {
      const blob = await downloadQuotationPdf(q.id);
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (win) win.onload = () => { win.focus(); win.print(); };
    } catch { toast.error('Error al imprimir PDF'); }
    finally { setDownloadingId(null); }
  };

  const addToCart = (product, variant) => {
    const existing = cart.find(i => i.product_id === product.id && i.size === variant.size && i.color === variant.color);
    if (existing) {
      setCart(cart.map(i => i === existing
        ? { ...i, quantity: i.quantity + 1, subtotal: i.unit_price * (i.quantity + 1) }
        : i
      ));
    } else {
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        size: variant.size,
        color: variant.color,
        subtotal: product.price,
      }]);
      toast.success(`${product.name} agregado`);
    }
  };

  const updateQuantity = (item, qty) => {
    if (qty <= 0) { setCart(cart.filter(i => i !== item)); return; }
    setCart(cart.map(i => i === item ? { ...i, quantity: qty, subtotal: i.unit_price * qty } : i));
  };

  const removeFromCart = (item) => setCart(cart.filter(i => i !== item));

  const total = cart.reduce((s, i) => s + i.subtotal, 0);

  const handleCreateQuotation = async () => {
    if (cart.length === 0) { toast.error('Debe agregar productos a la cotización'); return; }
    if (!selectedCustomer) { toast.error('Debe seleccionar un cliente'); return; }
    if (!validUntil) { toast.error('Debe especificar la fecha de validez'); return; }

    try {
      await createQuotation({
        customer_id:   selectedCustomer.id,
        customer_name: `${selectedCustomer.name} ${selectedCustomer.last_name || ''}`.trim(),
        items:         cart,
        valid_until:   validUntil,
        notes,
      }, user.id);
      toast.success('Cotización creada');
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al crear cotización');
    }
  };

  const resetForm = () => {
    setCart([]); setSelectedCustomer(null); setCustomerQuery('');
    setValidUntil(default30Days()); setNotes(''); setSearchTerm('');
    setCustomerResults([]); setShowCustomerDropdown(false);
  };

  const getStatusBadge = (status) => {
    const cfg = {
      pendiente:  { label: 'Pendiente',  cls: 'bg-yellow-100 text-yellow-800', Icon: Clock },
      convertida: { label: 'Convertida', cls: 'bg-green-100 text-green-800',  Icon: CheckCircle },
      rechazada:  { label: 'Rechazada',  cls: 'bg-red-100 text-red-800',      Icon: XCircle },
    }[status] || { label: status, cls: 'bg-slate-100 text-slate-700', Icon: Clock };
    return (
      <Badge className={cfg.cls}>
        <cfg.Icon className="w-3 h-3 mr-1" />
        {cfg.label}
      </Badge>
    );
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-screen">Cargando...</div>;

  return (
    <>
    <div className="p-6 md:p-8 space-y-6" data-testid="quotations-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Cotizaciones</h1>
        <Dialog open={isDialogOpen} onOpenChange={(v) => { setIsDialogOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button
              onClick={resetForm}
              className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-md shadow-cyan-500/20"
              data-testid="add-quotation-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nueva Cotización
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Nueva Cotización</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Products */}
              <div className="space-y-4">
                <div>
                  <Label>Buscar Productos</Label>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      placeholder="Buscar productos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="quotation-search-input"
                    />
                  </div>
                </div>
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {filteredProducts.map(product => (
                    <Card key={product.id} className="border border-slate-200">
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">{product.name}</p>
                            <p className="text-xs text-slate-400 mono-font">{product.code}</p>
                            <p className="text-sm text-slate-500">{product.category}</p>
                          </div>
                          <p className="font-bold text-cyan-600 mono-font">RD${product.price.toFixed(2)}</p>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(product.stock_variants || []).length > 0 ? (
                            (product.stock_variants).map((v, idx) => (
                              <Button
                                key={idx}
                                onClick={() => addToCart(product, v)}
                                className="text-xs bg-slate-900 hover:bg-slate-800"
                                size="sm"
                              >
                                {v.size} - {v.color} ({v.stock})
                              </Button>
                            ))
                          ) : (
                            <Button
                              onClick={() => addToCart(product, { size: '', color: '', stock: 0 })}
                              className="text-xs bg-slate-900 hover:bg-slate-800"
                              size="sm"
                            >
                              Agregar
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Cart / form */}
              <div className="space-y-4">
                {/* Customer search */}
                <div className="space-y-2">
                  <Label>Cliente *</Label>
                  <div className="relative">
                    <Input
                      placeholder="Buscar cliente por nombre, teléfono o correo..."
                      value={customerQuery}
                      onChange={(e) => { setCustomerQuery(e.target.value); setSelectedCustomer(null); }}
                      data-testid="quotation-customer-search"
                    />
                    {customerSearching && (
                      <p className="text-xs text-slate-400 mt-1">Buscando...</p>
                    )}
                    {showCustomerDropdown && customerResults.length > 0 && (
                      <div className="absolute z-50 w-full bg-white border border-slate-200 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                        {customerResults.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                            onClick={() => selectCustomer(c)}
                          >
                            <span className="font-medium">{c.name} {c.last_name || ''}</span>
                            {c.phone && <span className="text-slate-400 ml-2 text-xs">{c.phone}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedCustomer && (
                    <p className="text-xs text-emerald-600 font-medium">
                      ✓ {selectedCustomer.name} {selectedCustomer.last_name || ''}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Válida hasta *</Label>
                  <Input
                    type="date"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    data-testid="quotation-valid-until-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notas</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notas adicionales..."
                    data-testid="quotation-notes-input"
                  />
                </div>

                <div>
                  <Label className="mb-2 block">Productos ({cart.length})</Label>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {cart.map((item, idx) => (
                      <div key={idx} className="bg-slate-50 p-2 rounded border border-slate-200">
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.product_name}</p>
                            <p className="text-xs text-slate-500">{item.size} - {item.color}</p>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => removeFromCart(item)} className="h-6 w-6 text-rose-500">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <Button size="icon" variant="outline" onClick={() => updateQuantity(item, item.quantity - 1)} className="h-6 w-6">-</Button>
                            <span className="w-8 text-center font-bold mono-font">{item.quantity}</span>
                            <Button size="icon" variant="outline" onClick={() => updateQuantity(item, item.quantity + 1)} className="h-6 w-6">+</Button>
                          </div>
                          <span className="font-bold mono-font">RD${item.subtotal.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between text-xl font-bold">
                    <span>TOTAL:</span>
                    <span className="mono-font text-cyan-600">RD${total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleCreateQuotation}
                    className="flex-1 bg-cyan-500 hover:bg-cyan-600 text-white"
                    data-testid="create-quotation-button"
                  >
                    Crear Cotización
                  </Button>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="cancel-quotation-button">
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4" data-testid="quotations-list">
        {quotations.map(quotation => (
          <Card key={quotation.id} className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`quotation-card-${quotation.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <FileText className="w-5 h-5 text-cyan-500" />
                    {quotation.quotation_number}
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1 capitalize">{quotation.customer_name}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {getStatusBadge(quotation.status)}
                  <p className="text-2xl font-bold text-cyan-600 mono-font">RD${Number(quotation.total || 0).toFixed(2)}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Válida hasta</p>
                  <p className="font-medium">{quotation.valid_until || '—'}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Sucursal</p>
                  <p className="font-medium">{quotation.branch_name || '—'}</p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Creada por</p>
                  <p className="font-medium">{quotation.created_by_name || '—'}</p>
                </div>
              </div>

              <div className="border-t pt-3">
                <p className="text-sm font-semibold mb-2">Productos:</p>
                <div className="space-y-1">
                  {(quotation.items || []).map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm bg-slate-50 p-2 rounded">
                      <span>{item.product_name} {item.size ? `(${item.size}${item.color ? ' ' + item.color : ''})` : ''} ×{item.quantity}</span>
                      <span className="font-bold mono-font">RD${Number(item.subtotal || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {quotation.notes && (
                <p className="text-sm text-slate-500 italic">{quotation.notes}</p>
              )}

              <div className="flex gap-2 pt-2 border-t flex-wrap">
                {/* PDF actions — always visible */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePrint(quotation)}
                  disabled={downloadingId === quotation.id}
                  title="Imprimir"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  Imprimir
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(quotation)}
                  disabled={downloadingId === quotation.id}
                  title="Descargar PDF"
                >
                  <Download className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openEmailModal(quotation)}
                  title="Enviar por correo"
                >
                  <Mail className="w-4 h-4" />
                </Button>

                {/* Status actions — only for pendiente */}
                {quotation.status === 'pendiente' && (
                  <>
                    <Button
                      onClick={() => handleConvertir(quotation)}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white min-w-[140px]"
                      size="sm"
                      data-testid={`convert-quotation-${quotation.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Convertir a Factura
                    </Button>
                    <Button
                      onClick={() => handleStatusChange(quotation.id, 'rechazada')}
                      variant="outline"
                      size="sm"
                      className="text-rose-500 hover:bg-rose-50"
                      data-testid={`reject-quotation-${quotation.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Rechazar
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {quotations.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
            No hay cotizaciones registradas
          </div>
        )}
      </div>
    </div>

    <Dialog open={!!emailModal} onOpenChange={() => setEmailModal(null)}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Enviar cotización</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-600">
            Enviando <strong>{emailModal?.quotation_number}</strong> por correo electrónico
          </p>
          <div className="space-y-2">
            <Label>Correo del destinatario</Label>
            <Input
              type="email"
              value={emailAddress}
              onChange={e => setEmailAddress(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendEmail()}
              placeholder="correo@ejemplo.com"
              autoFocus
            />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setEmailModal(null)}>Cancelar</Button>
          <Button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
          >
            <Mail className="w-4 h-4 mr-2" />
            {sendingEmail ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default Quotations;
